/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getApi } from '../common/requestHelper';
import {
  AgentApi,
  WorkflowDetailDto,
  ListWorkflowsResponseDto,
  ImportWorkflowDto,
  WorkflowChatMessageDto,
  WorkflowErrorDto,
  UpdateWorkflowDto,
} from '@ink-ai/insight-service-sdk';
import { app } from './app';
import { RootState } from '.';
import {
  ChatInterfaceProps,
  ChatMessage,
} from '../insight-hub/components/workflow/types';
import i18n from 'i18next';
import { getFileType, reference } from './reference';

const CHAT_MESSAGE_PAGE_SIZE = 30;

export type WorkflowState = {
  workflows: WorkflowDetailDto[];
  loading: boolean;
  currentWorkflow: {
    running: boolean;
    waitingResponse: boolean;
    conversationId?: string;
    messages: ChatMessage[];
    taskId?: string;
    loadingMessages: boolean;
    hasMore: boolean;
    conversations: {
      data: ChatInterfaceProps['history'];
      loading: boolean;
    };
  };
};

const initialState: WorkflowState = {
  workflows: [],
  loading: false,
  currentWorkflow: {
    running: false,
    waitingResponse: false,
    messages: [],
    loadingMessages: false,
    conversations: {
      data: [],
      loading: false,
    },
    hasMore: false,
  },
};

export const listWorkflows = createAsyncThunk<ListWorkflowsResponseDto>(
  'workflow/listWorkflows',
  async () => {
    const workflowApi = await getApi(AgentApi);
    const response = await workflowApi.listAgents();
    return response.data;
  },
);

interface InvokeWorkflowProps {
  workflowId: string;
  query: string;
}

export const invokeWorkflow = createAsyncThunk(
  'workflow/invokeWorkflow',
  async (
    { query, workflowId }: InvokeWorkflowProps,
    { dispatch, getState },
  ) => {
    const state = getState() as RootState;
    try {
      const workflowApi = await getApi(AgentApi);
      const selectedFiles = state.reference.fileList.filter(({ id }) =>
        state.reference.selectedFileIds.includes(id),
      );
      const userMessage: ChatMessage = {
        id: Date.now().toString(),
        content: query,
        sender: 'user',
        timestamp: new Date().getTime(),
        status: 'sent',
        files: selectedFiles.map(({ id, name }) => ({
          id,
          name,
          format: getFileType(name),
        })),
      };
      dispatch(workflow.actions.appendMessage(userMessage));
      const res = await workflowApi.sendAgentChatMessage({
        instanceId: state.auth.instanceId,
        workflowId,
        conversationId: state.workflow.currentWorkflow.conversationId,
        query,
        fileIds: state.reference.selectedFileIds,
      });
      dispatch(reference.actions.clearChecked());
      return res.data;
    } catch (error: any) {
      dispatch(
        workflow.actions.appendMessage({
          id: new Date().toDateString(),
          content: error.message,
          title: error.name,
          sender: 'ai',
          timestamp: new Date().getTime(),
          status: 'error',
        }),
      );
      throw error;
    }
  },
);

export const stopWorkflow = createAsyncThunk(
  'workflow/stopWorkflow',
  async (workflowId: string, { getState }) => {
    const state = getState() as RootState;
    const workflowApi = await getApi(AgentApi);
    await workflowApi.stopAgentGenerate({
      workflowId,
      taskId: state.workflow.currentWorkflow.taskId,
    });
  },
);

export const fetchWorkflowConversations = createAsyncThunk(
  'workflow/fetchWorkflowConversations',
  async (workflowId: string) => {
    const workflowApi = await getApi(AgentApi);
    const response = await workflowApi.listAgentConversations(workflowId);
    return response.data;
  },
);

export const fetchConversationHistory = createAsyncThunk(
  'workflow/fetchConversationHistory',
  async (conversationId: string, { getState }) => {
    const state = getState() as RootState;
    const workflowApi = await getApi(AgentApi);
    const res = await workflowApi.getAgentConversationHistory(
      conversationId,
      CHAT_MESSAGE_PAGE_SIZE,
      state.workflow.currentWorkflow.messages[0]?.id,
    );
    return res.data;
  },
);

interface DeleteWorkflowConversationProps {
  workflowId: string;
  conversationId: string;
  onSuccess?: () => void;
}

export const deleteWorkflowConversation = createAsyncThunk(
  'workflow/deleteWorkflowConversation',
  async (
    { workflowId, conversationId, onSuccess }: DeleteWorkflowConversationProps,
    { dispatch },
  ) => {
    try {
      const workflowApi = await getApi(AgentApi);
      await workflowApi.deleteAgentConversation({
        workflowId,
        conversationId,
      });
      dispatch(
        app.actions.setGlobalMessage({
          message: i18n.t('chat.deleteSuccess'),
          status: 'success',
        }),
      );
      // Remove conversation from state
      dispatch(workflow.actions.removeConversation(conversationId));
      onSuccess?.();
    } catch (error: any) {
      dispatch(
        app.actions.setGlobalMessage({
          message: `${i18n.t('chat.deleteFailed')}: ${error.message}`,
          status: 'error',
        }),
      );
      throw error;
    }
  },
);

export const deleteWorkflow = createAsyncThunk(
  'workflow/deleteWorkflow',
  async (workflowIds: string[], { getState, dispatch }) => {
    try {
      const state = getState() as { workflow: WorkflowState };
      const workflowApi = await getApi(AgentApi);

      const deletePromises = workflowIds.map(async (uuid) => {
        const selectedWorkflow = state.workflow.workflows.find(
          (w) => w.workflowId === uuid,
        );

        if (selectedWorkflow) {
          await workflowApi.deleteAgent({
            workflowId: selectedWorkflow.workflowId,
          });
        }
      });

      await Promise.all(deletePromises);
      dispatch(listWorkflows());
    } catch (error: any) {
      console.error('Error occurred during deleteWorkflow:', error);
    }
  },
);

export const importWorkflow = createAsyncThunk(
  'workflow/importWorkflow',
  async (params: ImportWorkflowDto, { dispatch }) => {
    try {
      const workflowApi = await getApi(AgentApi);
      await workflowApi.importAgent(params);
      dispatch(
        app.actions.setGlobalMessage({
          message: 'Workflow imported successfully.',
          status: 'success',
        }),
      );
      dispatch(app.actions.setNavigationPath('/workflows'));
    } catch (error: any) {
      console.error('Error occurred during importWorkflow:', error);
      dispatch(
        app.actions.setGlobalMessage({
          message: 'Failed to import the workflow.' + error.message,
          status: 'error',
        }),
      );
    }
    dispatch(workflow.actions.setLoading(false));
  },
);

export const updateWorkflow = createAsyncThunk(
  'workflow/updateWorkflow',
  async (params: UpdateWorkflowDto, { dispatch }) => {
    try {
      const workflowApi = await getApi(AgentApi);
      await workflowApi.updateAgent(params);
      dispatch(
        app.actions.setGlobalMessage({
          message: 'Workflow updated successfully.',
          status: 'success',
        }),
      );
      dispatch(app.actions.setNavigationPath('/workflows'));
    } catch (error: any) {
      console.error('Error occurred during updateWorkflow:', error);
      dispatch(
        app.actions.setGlobalMessage({
          message: 'Failed to update the workflow: ' + error.message,
          status: 'error',
        }),
      );
    }
    dispatch(workflow.actions.setLoading(false));
  },
);

/**
 * Removes all <think> tags after the first occurrence
 * @param content The string content to process
 * @returns The string with subsequent <think> tags removed
 */
const keepFirstThinkTag = (content: string): string => {
  const parts = content.split('<think>');
  if (parts.length <= 1) {
    return content;
  }
  return parts[0] + '<think>' + parts.slice(1).join('');
};

export const workflow = createSlice({
  name: 'workflow',
  initialState,
  reducers: {
    setLoading(state, { payload }: PayloadAction<boolean>) {
      state.loading = payload;
    },
    setConversationId(state, { payload }: PayloadAction<string>) {
      state.currentWorkflow.conversationId = payload;
    },
    clearCurrentWorkflow(state) {
      state.currentWorkflow = initialState.currentWorkflow;
    },
    clearChatHistory(state) {
      state.currentWorkflow = {
        ...initialState.currentWorkflow,
        conversations: state.currentWorkflow.conversations,
      };
    },
    onAiMessageUpdate(
      state,
      { payload }: PayloadAction<WorkflowChatMessageDto>,
    ) {
      if (payload.conversationId !== state.currentWorkflow.conversationId) {
        console.warn(
          `[On Workflow Message] Conversation ID mismatch, payload: ${payload.conversationId}, current: ${state.currentWorkflow.conversationId}`,
        );
        return;
      }
      if (payload.taskId !== state.currentWorkflow.taskId) {
        console.warn('[On Workflow Message] Task ID mismatch.');
        return;
      }
      let targetMessage: ChatMessage | undefined;
      const messageId = payload.messageId ?? Date.now().toString();

      targetMessage = state.currentWorkflow.messages.find(
        ({ id, status }) => id === messageId && status === 'sending',
      );
      if (!targetMessage) {
        targetMessage =
          state.currentWorkflow.messages[
            state.currentWorkflow.messages.length - 1
          ];
      }
      // create new message
      if (
        !targetMessage ||
        targetMessage.id !== messageId ||
        targetMessage.sender === 'user'
      ) {
        state.currentWorkflow.messages = [
          ...state.currentWorkflow.messages,
          {
            id: messageId,
            content: keepFirstThinkTag(payload.content),
            title: payload.nodeTitle,
            sender: 'ai',
            timestamp: new Date().getTime(),
            status: 'sending',
          },
        ];
      } else if (targetMessage.status === 'sending') {
        // add new line after thinking message for rendering
        targetMessage.content += payload.content.replace(
          '</think>',
          '\n\n</think>\n\n',
        );
        targetMessage.content = keepFirstThinkTag(targetMessage.content);
      }

      state.currentWorkflow.waitingResponse = false;
    },
    onNodeFinish(state, { payload }: PayloadAction<WorkflowChatMessageDto>) {
      if (payload.conversationId !== state.currentWorkflow.conversationId) {
        console.warn(
          `[On Node Finish] Conversation ID mismatch, payload: ${payload.conversationId}, current: ${state.currentWorkflow.conversationId}`,
        );
        return;
      }
      if (payload.taskId !== state.currentWorkflow.taskId) {
        console.warn('[On Node Finish] Task ID mismatch.');
        return;
      }
      const messageIndex = state.currentWorkflow.messages.findIndex(
        ({ id }) => id === payload.messageId,
      );
      const newMessage: ChatMessage = {
        id: payload.messageId,
        // add new line after thinking message for rendering
        content: keepFirstThinkTag(
          payload.content.replace('</think>', '\n\n</think>\n\n'),
        ),
        title: payload.nodeTitle,
        sender: 'ai',
        timestamp: new Date().getTime(),
        status: 'sent',
      };

      if (messageIndex === -1) {
        state.currentWorkflow.messages = [
          ...state.currentWorkflow.messages,
          newMessage,
        ];
      } else {
        state.currentWorkflow.messages = [
          ...state.currentWorkflow.messages.slice(0, messageIndex),
          newMessage,
          ...state.currentWorkflow.messages.slice(messageIndex + 1),
        ];
      }
    },
    onWorkflowStart(state, { payload }: PayloadAction<WorkflowChatMessageDto>) {
      state.currentWorkflow.conversationId = payload.conversationId;
      state.currentWorkflow.taskId = payload.taskId;
    },
    onWorkflowFinish(
      state,
      { payload }: PayloadAction<WorkflowChatMessageDto>,
    ) {
      if (
        state.currentWorkflow.conversationId &&
        payload.conversationId !== state.currentWorkflow.conversationId
      ) {
        console.warn(
          `[On Workflow Finish] Conversation ID mismatch, payload: ${payload.conversationId}, current: ${state.currentWorkflow.conversationId}`,
        );
        return;
      }
      state.currentWorkflow.running = false;
      state.currentWorkflow.waitingResponse = false;
    },
    onWorkflowError(state, { payload }: PayloadAction<WorkflowErrorDto>) {
      if (
        state.currentWorkflow.conversationId &&
        payload.conversationId !== state.currentWorkflow.conversationId
      ) {
        console.warn(
          `[On Workflow Error] Conversation ID mismatch, payload: ${payload.conversationId}, current: ${state.currentWorkflow.conversationId}`,
        );
        return;
      }
      state.currentWorkflow.messages = [
        ...state.currentWorkflow.messages,
        {
          id: new Date().toDateString(),
          content: payload.message,
          title: `Error ${payload.code}`,
          sender: 'ai',
          timestamp: new Date().getTime(),
          status: 'error',
        },
      ];
      state.currentWorkflow.running = false;
      state.currentWorkflow.waitingResponse = false;
    },
    appendMessage(state, { payload }: PayloadAction<ChatMessage>) {
      state.currentWorkflow.messages = [
        ...state.currentWorkflow.messages,
        payload,
      ];
    },
    removeConversation(
      state,
      { payload: conversationId }: PayloadAction<string>,
    ) {
      state.currentWorkflow.conversations.data =
        state.currentWorkflow.conversations.data.filter(
          (conv) => conv.id !== conversationId,
        );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(invokeWorkflow.pending, (state) => {
        state.currentWorkflow.running = true;
        state.currentWorkflow.waitingResponse = true;
      })
      .addCase(invokeWorkflow.rejected, (state) => {
        state.currentWorkflow.running = false;
        state.currentWorkflow.waitingResponse = false;
      })
      .addCase(invokeWorkflow.fulfilled, (state, { payload }) => {
        state.currentWorkflow.conversationId = payload.id;
        if (
          // new conversation
          !state.currentWorkflow.conversations.data.some(
            ({ id }) => id === payload.id,
          )
        ) {
          state.currentWorkflow.conversations.data = [
            {
              id: payload.id,
              title: payload.name,
              updatedAt: payload.updatedAt,
            },
            ...state.currentWorkflow.conversations.data,
          ];
        }
      })
      .addCase(listWorkflows.pending, (state) => {
        state.loading = true;
      })
      .addCase(listWorkflows.fulfilled, (state, action) => {
        state.workflows = action.payload.data;
        state.loading = false;
      })
      .addCase(listWorkflows.rejected, (state) => {
        state.loading = false;
      })
      .addCase(stopWorkflow.fulfilled, (state) => {
        state.currentWorkflow.taskId = undefined;
      })
      .addCase(fetchConversationHistory.pending, (state) => {
        state.currentWorkflow.loadingMessages = true;
      })
      .addCase(fetchConversationHistory.rejected, (state) => {
        state.currentWorkflow.loadingMessages = false;
        // fail to fetch conversation, new conversation by default
        state.currentWorkflow.conversationId = undefined;
      })
      .addCase(fetchConversationHistory.fulfilled, (state, { payload }) => {
        if (
          state.currentWorkflow.conversationId &&
          payload.data.some(
            ({ conversationId }) =>
              conversationId !== state.currentWorkflow.conversationId,
          )
        ) {
          console.warn(
            '[fetchConversationHistory] Conversation ID mismatch, aborting',
          );
          return;
        }
        // remove greeting message
        state.currentWorkflow.messages = state.currentWorkflow.messages.filter(
          ({ id }) => id !== 'init',
        );
        state.currentWorkflow.messages.unshift(
          ...payload.data
            .map(
              (message): ChatMessage => ({
                id: message.id,
                content: keepFirstThinkTag(
                  message.content.replace('</think>', '\n\n</think>\n\n'),
                ),
                title: message.title,
                sender: message.sender,
                timestamp: message.timestamp,
                status: message.status,
                files: message.messageFiles,
              }),
            )
            // reverse for put newer message to the bottom
            .reverse(),
        );

        state.currentWorkflow.loadingMessages = false;
        if (payload.data.length < CHAT_MESSAGE_PAGE_SIZE) {
          state.currentWorkflow.hasMore = false;
        } else {
          state.currentWorkflow.hasMore = true;
        }
      })
      .addCase(fetchWorkflowConversations.pending, (state) => {
        state.currentWorkflow.conversations.loading = true;
      })
      .addCase(fetchWorkflowConversations.fulfilled, (state, { payload }) => {
        state.currentWorkflow.conversations.data = payload.data.map((conv) => ({
          id: conv.id,
          title: conv.name || i18n.t('chat.newChat'),
          updatedAt: conv.updatedAt,
        }));
        state.currentWorkflow.conversations.loading = false;
      })
      .addCase(fetchWorkflowConversations.rejected, (state) => {
        state.currentWorkflow.conversations.loading = false;
      });
  },
});

export const workflowActions = workflow.actions;
